import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import StatusBadge from '@/components/base/StatusBadge';
import Button from '@/components/base/Button';
import PlatformIcon from '@/components/base/PlatformIcon';
import { formatCompact, formatMoney } from '@/utils/format';
import type { CreatorProfile } from '@/types/portal';
import { EngagementMeter, FitRing, ReachBar } from './CreatorVisuals';
import { bestViews, engagementVerdict, readiness } from './creatorInsights';

interface CreatorResultCardProps {
  creator: CreatorProfile;
  rank: number;
  inCompare: boolean;
  saved: boolean;
  onToggleCompare: () => void;
  onToggleSave: () => void;
  /** Whether the creator has already been added to the active campaign. */
  inCampaign?: boolean;
  /** Adds the creator to a campaign (or opens the campaign picker). */
  onAddToCampaign?: () => void;
}

/**
 * A creator in the list view, laid out to answer, in order: who is this (photo, bio), how big and
 * how engaged (reach by platform, engagement in plain words, views, rate), what working with them
 * is like (formats, reply time, record with this agency), why they fit, any risks, and can we
 * hire them now. Fields production doesn't hold yet are left out rather than shown empty.
 */
export default function CreatorResultCard({
  creator,
  rank,
  inCompare,
  saved,
  onToggleCompare,
  onToggleSave,
  inCampaign = false,
  onAddToCampaign,
}: CreatorResultCardProps) {
  const navigate = useNavigate();
  const openProfile = () => navigate(`/creators/${creator.id}`, { state: { from: 'discovery', creator } });
  const ready = readiness(creator);
  const engagement = engagementVerdict(creator.engagementRate);
  const views = bestViews(creator);
  const history = creator.agencyHistory;
  const subtitle = [creator.handle, creator.city, creator.categories.slice(0, 2).join(' · ')].filter(Boolean).join(' · ');

  return (
    <article
      className="creator-card group rounded-xl border border-background-200 bg-background-50 p-4 md:p-5"
      style={{ animationDelay: `${Math.min(rank - 1, 10) * 60}ms` }}
    >
      {/* Who they are */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={openProfile}
          className="relative h-fit shrink-0 cursor-pointer"
          aria-label={`Open ${creator.name}'s profile`}
        >
          <span className="block rounded-full ring-2 ring-transparent ring-offset-2 transition-[box-shadow] duration-300 group-hover:ring-primary-300">
            <Avatar src={creator.avatar} alt={creator.name} size="lg" />
          </span>
          <span
            className={`absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full border-2 border-background-50 ${ready.ready ? 'bg-accent-500' : 'bg-secondary-400'}`}
            title={ready.label}
          />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-[11px] font-label font-semibold text-foreground-400">#{rank}</span>
                <button
                  type="button"
                  onClick={openProfile}
                  className="cursor-pointer font-heading text-base font-semibold text-foreground-950 hover:text-primary-700"
                >
                  {creator.name}
                </button>
                {creator.verification === 'Verified' ? (
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-label text-accent-700">
                    <i className="ri-verified-badge-fill text-[13px]" />
                    Verified
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 truncate text-xs text-foreground-500">{subtitle}</p>
            </div>
            <FitRing score={creator.fitScore} />
          </div>
          {creator.bio ? <p className="mt-1.5 line-clamp-2 text-sm leading-snug text-foreground-700">{creator.bio}</p> : null}
        </div>
      </div>

      {/* How big and how engaged */}
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg bg-background-100 px-4 py-3 sm:grid-cols-4">
        <div>
          <dt className="text-[11px] text-foreground-500">Reach</dt>
          <dd className="font-heading text-base font-semibold text-foreground-950">{formatCompact(creator.followers)}</dd>
          <ReachBar platforms={creator.platforms} className="mt-1.5" />
        </div>
        <div>
          <dt className="text-[11px] text-foreground-500">Engagement</dt>
          <dd className="flex items-baseline gap-1.5">
            <span className="font-heading text-base font-semibold text-foreground-950">{creator.engagementRate}%</span>
            <span className={`text-[11px] font-semibold ${engagement.tone}`}>{engagement.label}</span>
          </dd>
          <div className="mt-1.5" title="Usual range is 1–3%">
            <EngagementMeter rate={creator.engagementRate} />
          </div>
        </div>
        <div>
          <dt className="text-[11px] text-foreground-500">Avg views</dt>
          <dd className="font-heading text-base font-semibold text-foreground-950">{views ? formatCompact(views.avgViews) : '—'}</dd>
          {views ? <p className="text-[11px] text-foreground-500">per post on {views.platform}</p> : null}
        </div>
        <div>
          <dt className="text-[11px] text-foreground-500">Est. rate</dt>
          <dd className="font-heading text-base font-semibold text-foreground-950">
            {creator.rate != null ? formatMoney(creator.rate, creator.currency) : '—'}
          </dd>
          <p className="text-[11px] text-foreground-500">{creator.rate != null ? 'last agreed with you' : 'no fee agreed yet'}</p>
        </div>
      </dl>

      {/* Each platform */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {creator.platforms.map((account) => (
          <span
            key={`${account.platform}-${account.handle}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600"
          >
            <PlatformIcon platform={account.platform} className="text-[13px]" />
            <span className="font-label font-medium text-foreground-800">{account.platform}</span>
            {account.verified ? <i className="ri-verified-badge-fill text-[12px] text-accent-600" aria-label="verified" /> : null}
            {formatCompact(account.followers)} · {account.engagementRate}%
            {account.avgViews > 0 ? <span className="text-foreground-400">· {formatCompact(account.avgViews)} views</span> : null}
          </span>
        ))}
      </div>

      {/* Working with them */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-foreground-600">
        {creator.contentFormats.length > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-film-line text-foreground-400" aria-hidden="true" />
            {creator.contentFormats.join(' · ')}
          </span>
        ) : null}
        {creator.responseTime ? (
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-chat-smile-2-line text-foreground-400" aria-hidden="true" />
            Replies in {creator.responseTime.charAt(0).toLowerCase() + creator.responseTime.slice(1)}
          </span>
        ) : null}
        {history.campaigns > 0 ? (
          <span className="inline-flex items-center gap-1.5 font-medium text-foreground-800">
            <i className="ri-medal-line text-secondary-600" aria-hidden="true" />
            {history.campaigns} {history.campaigns === 1 ? 'campaign' : 'campaigns'} with you
            {history.onTimeRate != null ? ` · ${history.onTimeRate}% on time` : ''}
            {history.rating != null ? (
              <span className="inline-flex items-center gap-0.5 text-secondary-600">
                · <i className="ri-star-fill" aria-hidden="true" />
                {history.rating.toFixed(1)}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-foreground-500">
            <i className="ri-user-star-line text-foreground-400" aria-hidden="true" />
            New to your agency
          </span>
        )}
        {creator.languages.length > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-translate-2 text-foreground-400" aria-hidden="true" />
            {creator.languages.join(', ')}
          </span>
        ) : null}
      </div>

      {/* Why they fit, and anything to watch */}
      {creator.reasonCodes.length > 0 || creator.riskFlags.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {creator.reasonCodes.slice(0, 4).map((reason) => (
            <Badge key={reason} tone="accent" icon="ri-check-double-line">
              {reason}
            </Badge>
          ))}
          {creator.riskFlags.map((flag) => (
            <StatusBadge key={flag} status={flag} />
          ))}
        </div>
      ) : null}

      {/* Can we hire them now, and what to do */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-100 pt-3">
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-medium ${ready.ready ? 'text-accent-700' : 'text-secondary-800'}`}
          title={`KYC: ${creator.kycStatus} · Wallet: ${creator.walletStatus}`}
        >
          <i className={ready.ready ? 'ri-checkbox-circle-fill' : 'ri-time-line'} aria-hidden="true" />
          {ready.label}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onToggleSave}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-md border cursor-pointer ${
              saved
                ? 'border-secondary-400 bg-secondary-100 text-secondary-900'
                : 'border-background-300 text-foreground-500 hover:bg-background-100'
            }`}
            aria-label={saved ? 'Remove from saved' : 'Save creator'}
          >
            <i className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
          </button>
          <Button
            variant={inCompare ? 'secondary' : 'outline'}
            size="sm"
            icon={inCompare ? 'ri-check-line' : 'ri-scales-3-line'}
            onClick={onToggleCompare}
          >
            {inCompare ? 'In compare' : 'Compare'}
          </Button>
          <Button size="sm" icon="ri-user-line" onClick={openProfile}>
            View profile
          </Button>
          {onAddToCampaign ? (
            <Button
              size="sm"
              variant={inCampaign ? 'secondary' : 'accent'}
              icon={inCampaign ? 'ri-check-line' : 'ri-user-add-line'}
              onClick={onAddToCampaign}
              disabled={inCampaign}
            >
              {inCampaign ? 'Added' : 'Add to campaign'}
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
